import * as React from 'react';
import * as api from '../services/api-client';

/**
 * Subdomain state management using React Context + useReducer.
 * Manages the list of subdomains assigned to the authenticated user.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Subdomain {
  readonly subdomainId: string;
  readonly subdomainLabel: string;
  readonly fqdn: string;
  readonly domainId: string;
  readonly assignedAt: string;
}

// ─── State ───────────────────────────────────────────────────────────────────

export interface SubdomainState {
  readonly subdomains: readonly Subdomain[];
  readonly isLoading: boolean;
  readonly error: string | null;
}

export const initialSubdomainState: SubdomainState = {
  subdomains: [],
  isLoading: false,
  error: null,
};

// ─── Actions ─────────────────────────────────────────────────────────────────

type SubdomainAction =
  | { readonly type: 'SUBDOMAINS_LOADING' }
  | { readonly type: 'SUBDOMAINS_LOADED'; readonly payload: readonly Subdomain[] }
  | { readonly type: 'SUBDOMAINS_ERROR'; readonly payload: string };

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function subdomainReducer(state: SubdomainState, action: SubdomainAction): SubdomainState {
  switch (action.type) {
    case 'SUBDOMAINS_LOADING':
      return { ...state, isLoading: true, error: null };
    case 'SUBDOMAINS_LOADED':
      return { subdomains: action.payload, isLoading: false, error: null };
    case 'SUBDOMAINS_ERROR':
      return { ...state, isLoading: false, error: action.payload };
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface SubdomainContextValue {
  readonly state: SubdomainState;
  readonly loadSubdomains: () => Promise<void>;
  readonly isConnecting: boolean;
}

const SubdomainContext = React.createContext<SubdomainContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

interface SubdomainProviderProps {
  readonly children: React.ReactNode;
}

/**
 * SubdomainProvider fetches and manages the list of user-assigned subdomains.
 * Includes retry logic for cold-start failures (up to 3 attempts with backoff).
 */
export function SubdomainProvider({ children }: SubdomainProviderProps): React.JSX.Element {
  const [state, dispatch] = React.useReducer(subdomainReducer, initialSubdomainState);
  const [isConnecting, setIsConnecting] = React.useState(false);

  const loadSubdomains = React.useCallback(async (): Promise<void> => {
    dispatch({ type: 'SUBDOMAINS_LOADING' });

    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 2000, 3000]; // ms

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const data = await api.get<{ subdomains: Subdomain[] }>('/user/subdomains');
        dispatch({ type: 'SUBDOMAINS_LOADED', payload: data.subdomains });
        setIsConnecting(false);
        return;
      } catch (err) {
        const isRetryable =
          err instanceof api.NetworkError ||
          (err instanceof api.ApiErrorResponse && err.status >= 500);

        if (isRetryable && attempt < MAX_RETRIES) {
          setIsConnecting(true);
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS[attempt]));
          continue;
        }

        setIsConnecting(false);
        dispatch({
          type: 'SUBDOMAINS_ERROR',
          payload: err instanceof Error ? err.message : 'Failed to load subdomains',
        });
        return;
      }
    }
  }, []);

  // Load subdomains on mount
  React.useEffect(() => {
    loadSubdomains();
  }, [loadSubdomains]);

  const contextValue = React.useMemo(
    (): SubdomainContextValue => ({ state, loadSubdomains, isConnecting }),
    [state, loadSubdomains, isConnecting],
  );

  return React.createElement(SubdomainContext.Provider, { value: contextValue }, children);
}

// ─── Consumer Hook ───────────────────────────────────────────────────────────

/**
 * Hook to access subdomain state and actions.
 * Must be used within a SubdomainProvider.
 */
export function useSubdomains(): SubdomainContextValue {
  const context = React.useContext(SubdomainContext);
  if (!context) {
    throw new Error('useSubdomains must be used within a SubdomainProvider');
  }
  return context;
}
